import * as React from "react";
import dayjs, { locale, Dayjs } from "dayjs";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { IEvent } from "../interfaces/IEvent";
import { Typography } from "@mui/material";
import ListEvents from "../features/events/listEvents";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import splLogo from "../../images/spl_iso.jpg";
import { NATIONAL_ORGANIZERS } from "../consts/tournamentConsts";

interface TournamentCalendarProps {
  events: IEvent[];
}

const MONTH_NAMES = [
  "Tammikuu", "Helmikuu", "Maaliskuu", "Huhtikuu",
  "Toukokuu", "Kesäkuu", "Heinäkuu", "Elokuu",
  "Syyskuu", "Lokakuu", "Marraskuu", "Joulukuu",
];

interface CustomHeaderProps {
  currentMonth: Dayjs;
  onMonthChange: (date: Dayjs, slideDirection: "left" | "right") => void;
  years?: number[];
  selectedYear?: number;
  selectedMonth?: number;
  onNavigate?: (year: number, month: number) => void;
  onToday?: () => void;
}

const CustomCalendarHeader: React.FC<CustomHeaderProps> = ({
  currentMonth,
  onMonthChange,
  years = [],
  selectedYear = dayjs().year(),
  selectedMonth = dayjs().month(),
  onNavigate,
  onToday,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", px: 1, pt: 2, pb: 1 }}>
    {(selectedYear !== dayjs().year() || selectedMonth !== dayjs().month()) && (
      <Box sx={{ mb: "6px", pb: "6px", pl: 5 }}>
        <Chip label="Tänään" size="small" onClick={onToday} />
      </Box>
    )}
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <IconButton
        size="small"
        onClick={() => onMonthChange(currentMonth.subtract(1, "month"), "right")}
      >
        <ChevronLeftIcon />
      </IconButton>
      <Box sx={{ display: "flex", gap: 1, flex: 1, justifyContent: "center" }}>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel>Vuosi</InputLabel>
          <Select
            value={selectedYear}
            label="Vuosi"
            onChange={(e) => onNavigate?.(Number(e.target.value), selectedMonth)}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Kuukausi</InputLabel>
          <Select
            value={selectedMonth}
            label="Kuukausi"
            onChange={(e) => onNavigate?.(selectedYear, Number(e.target.value))}
          >
            {MONTH_NAMES.map((name, i) => (
              <MenuItem key={i} value={i}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <IconButton
        size="small"
        onClick={() => onMonthChange(currentMonth.add(1, "month"), "left")}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  </Box>
);

const TournamentCalendar: React.FC<TournamentCalendarProps> = (props) => {
  const today = dayjs();

  const oldestYear = props.events.reduce((min, e) => {
    const y = dayjs(e.startingTime).year();
    return isNaN(y) ? min : Math.min(min, y);
  }, today.year());
  const latestYear = today.year() + 3;
  const years = Array.from(
    { length: latestYear - oldestYear + 1 },
    (_, i) => oldestYear + i
  );

  const [value, setValue] = React.useState(today);
  const [selectedYear, setSelectedYear] = React.useState(today.year());
  const [selectedMonth, setSelectedMonth] = React.useState(today.month());

  const highlightsForDate = (date: Dayjs) =>
    props.events
      .filter((e) => dayjs(e.startingTime).format("YYYYMM") === date.format("YYYYMM"))
      .map((e) => dayjs(e.startingTime).get("date"));

  const nationalDaysForDate = (date: Dayjs) =>
    props.events
      .filter(
        (e) =>
          dayjs(e.startingTime).format("YYYYMM") === date.format("YYYYMM") &&
          e.organizerClubs.some((o) => NATIONAL_ORGANIZERS.includes(o))
      )
      .map((e) => dayjs(e.startingTime).get("date"));

  const [highlightedDays, setHighlightedDays] = React.useState(() =>
    highlightsForDate(today)
  );
  const [nationalDays, setNationalDays] = React.useState(() =>
    nationalDaysForDate(today)
  );

  const navigateTo = (year: number, month: number) => {
    const next = value.year(year).month(month);
    setValue(next);
    setSelectedYear(year);
    setSelectedMonth(month);
    setHighlightedDays(highlightsForDate(next));
    setNationalDays(nationalDaysForDate(next));
  };

  const valueEvents = () => ({
    events: props.events.filter(
      (e) =>
        dayjs(e.startingTime).format("YYYYMMDD") === value.format("YYYYMMDD")
    ),
  });

  const isPast = () => value.isBefore(today.add(-1, "day"));

  function ServerDay(
    props: PickersDayProps<Dayjs> & { highlightedDays?: number[]; nationalDays?: number[] }
  ) {
    const { highlightedDays = [], nationalDays = [], day, outsideCurrentMonth, ...other } = props;
    const dayNum = props.day.date();
    const isSelected = !props.outsideCurrentMonth && highlightedDays.indexOf(dayNum) >= 0;
    const isNational = !props.outsideCurrentMonth && nationalDays.indexOf(dayNum) >= 0;
    const dot = isSelected ? (
      isNational
        ? <img src={splLogo} alt="SPL" style={{ width: 12, height: 12, borderRadius: "50%", display: "block" }} />
        : <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "black" }} />
    ) : undefined;
    return (
      <Badge
        key={props.day.toString()}
        overlap="circular"
        badgeContent={dot}
      >
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
        />
      </Badge>
    );
  }

  locale("fi");
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mt: 2, mb: 2 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateCalendar
          value={value}
          displayWeekNumber
          onChange={(newValue: any) => {
            setValue(newValue);
            setSelectedYear(newValue.year());
            setSelectedMonth(newValue.month());
          }}
          onMonthChange={(month) => {
            setSelectedYear(month.year());
            setSelectedMonth(month.month());
            setHighlightedDays(highlightsForDate(month));
            setNationalDays(nationalDaysForDate(month));
          }}
          timezone="Europe/Helsinki"
          views={["day"]}
          dayOfWeekFormatter={(_day, weekday: any) =>
            `${(weekday as dayjs.Dayjs).format("dd")}`
          }
          slots={{ day: ServerDay, calendarHeader: CustomCalendarHeader }}
          slotProps={{
            day: { highlightedDays, nationalDays } as any,
            calendarHeader: {
              years,
              selectedYear,
              selectedMonth,
              onNavigate: navigateTo,
              onToday: () => navigateTo(today.year(), today.month()),
            } as any,
          }}
          sx={{
            width: "330px",
            mx: 0,
            maxHeight: "none",
            overflow: "visible",
            "& .MuiDayCalendar-slideTransition": { minHeight: "294px" },
          }}
        />
      </LocalizationProvider>

      <Box sx={{ width: "100%" }}>
        <Typography variant="h6" sx={{ marginTop: 4, marginLeft: 2 }}>
          {value.format("D.M.YYYY")}{" "}
          {isPast() ? "järjestetyt turnaukset" : "järjestettävät turnaukset"}
        </Typography>
        <ListEvents
          name="upcoming-events"
          events={valueEvents()}
          canEdit={isPast() ? false : true}
        />
      </Box>
    </Box>
  );
};

export default TournamentCalendar;
